// frontend/src/components/LOS/Niyogin/NiyoginBRE.tsx
// Manual-trigger page: user selects bureau option, clicks button → BRE API fires → shows result.
// onDone() advances journey only if Niyogin returns HTTP 200.
// On failure the error is shown and the user stays here.
// Pre-fills from stored BRE result via value prop (getter: LOS_GetNiyoginBRE_306).

import React, { useState, useEffect } from "react";
import axios from "@/lib/axios";
import { API_BASE_URL } from "@/config";
import JourneyHeader from "@/components/JourneyHeader";

interface BREResult {
  bre_status: "PENDING" | "ELIGIBLE" | "REJECTED" | "ERROR";
  crif_score: string | null;
  max_eligible_loan_amount: string | null;
  max_loan_tenure: number | null;
  rejection_reasons: string[];
}

interface Props {
  value?: {
    bre_status?: string | null;
    crif_score?: string | null;
    max_eligible_loan_amount?: string | null;
    max_loan_tenure?: number | null;
    bre_rejection_reasons?: string[];
  };
  onChange?: (val: any) => void;
  disabled?: boolean;
  registerSubmit?: (fn: () => Promise<void>) => void;
  tenure?: number;
  loan_ticket_size?: number;
  onDone?: () => void;
}

const NiyoginBRE: React.FC<Props> = ({
  value,
  onChange,
  tenure = 4,
  loan_ticket_size = 100000,
  onDone,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BREResult | null>(null);
  // bureau: true = fetch full CRIF bureau report; false = skip bureau pull
  const [bureau, setBureau] = useState<boolean>(true);

  // Pre-fill from stored BRE result when component mounts or value changes
  useEffect(() => {
    if (value?.bre_status && value.bre_status !== null) {
      setResult({
        bre_status: value.bre_status as BREResult["bre_status"],
        crif_score: value.crif_score ?? null,
        max_eligible_loan_amount: value.max_eligible_loan_amount ?? null,
        max_loan_tenure: value.max_loan_tenure ?? null,
        rejection_reasons: value.bre_rejection_reasons ?? [],
      });
    }
  }, [value?.bre_status]);

  const handleClick = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/trigger_niyogin_bre/`, {
        tenure,
        loan_ticket_size,
        bureau,
      });
      // Backend only returns success=true when Niyogin returned HTTP 200
      if (res.data?.success === true) {
        const newResult: BREResult = {
          bre_status: res.data?.bre_status ?? "ERROR",
          crif_score: res.data?.crif_score ?? null,
          max_eligible_loan_amount: res.data?.max_eligible_loan_amount ?? null,
          max_loan_tenure: res.data?.max_loan_tenure ?? null,
          rejection_reasons: res.data?.bre_rejection_reasons ?? [],
        };
        setResult(newResult);
        // Propagate to JourneyPage localState so it's preserved across re-renders
        onChange?.({
          bre_status: newResult.bre_status,
          crif_score: newResult.crif_score,
          max_eligible_loan_amount: newResult.max_eligible_loan_amount,
          max_loan_tenure: newResult.max_loan_tenure,
          bre_rejection_reasons: newResult.rejection_reasons,
        });
      } else {
        setError(res.data?.error || "Eligibility check failed. Please try again.");
      }
    } catch (err: any) {
      // axios throws on 4xx/5xx — extract the Niyogin error message from response body
      const niyoginMsg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        (err instanceof Error ? err.message : String(err));
      console.error("[NiyoginBRE] Error:", niyoginMsg, err?.response?.data);
      setError(niyoginMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    onDone?.();
  };

  return (
    <div className="min-h-screen bg-intellend-white">
      <JourneyHeader />
      <main className="container mx-auto px-4 py-8 max-w-md">
        <div className="bg-white shadow-md rounded-lg p-8 space-y-6">
          {/* Icon + Title */}
          <div className="text-center space-y-2">
            <div className="text-5xl">📊</div>
            <h2 className="text-xl font-bold text-gray-800">Eligibility Check (BRE)</h2>
            <p className="text-sm text-gray-500">
              Check your eligibility with Niyogin's credit scoring engine.
            </p>
          </div>

          {/* Bureau Toggle — only shown before result */}
          {!result && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Bureau Report</p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setBureau(true)}
                  className={`flex-1 py-2 px-4 rounded-lg border text-sm font-medium transition-colors ${
                    bureau
                      ? "bg-intellend-green text-white border-intellend-green"
                      : "bg-white text-gray-600 border-gray-300 hover:border-intellend-green"
                  }`}
                >
                  ✅ With Bureau (bureau: true)
                </button>
                <button
                  type="button"
                  onClick={() => setBureau(false)}
                  className={`flex-1 py-2 px-4 rounded-lg border text-sm font-medium transition-colors ${
                    !bureau
                      ? "bg-gray-700 text-white border-gray-700"
                      : "bg-white text-gray-600 border-gray-300 hover:border-gray-700"
                  }`}
                >
                  ⬜ Without Bureau (bureau: false)
                </button>
              </div>
              <p className="text-xs text-gray-400">
                {bureau
                  ? "Full CRIF bureau report will be fetched."
                  : "Bureau pull will be skipped — faster but no CRIF score."}
              </p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-2">
              <p className="text-sm font-semibold text-red-700">❌ Eligibility Check Failed</p>
              <p className="text-sm text-red-600">{error}</p>
              <p className="text-xs text-red-500">Please correct the issue and try again.</p>
              <button
                className="block mt-1 text-red-600 underline text-xs"
                onClick={() => setError(null)}
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className={`rounded-lg p-4 border text-center space-y-2 ${
              result.bre_status === "ELIGIBLE"
                ? "bg-green-50 border-green-200"
                : result.bre_status === "REJECTED"
                ? "bg-red-50 border-red-200"
                : "bg-yellow-50 border-yellow-200"
            }`}>
              {result.bre_status === "ELIGIBLE" && (
                <>
                  <div className="text-3xl">✅</div>
                  <p className="font-semibold text-green-700">Eligible!</p>
                  {result.crif_score && (
                    <p className="text-sm text-gray-600">
                      CRIF Score: <span className="font-medium">{result.crif_score}</span>
                    </p>
                  )}
                  {result.max_eligible_loan_amount && (
                    <p className="text-sm text-gray-600">
                      Max Amount:{" "}
                      <span className="font-medium">
                        ₹{Number(result.max_eligible_loan_amount).toLocaleString("en-IN")}
                      </span>
                    </p>
                  )}
                  {result.max_loan_tenure && (
                    <p className="text-sm text-gray-600">
                      Max Tenure: <span className="font-medium">{result.max_loan_tenure} days</span>
                    </p>
                  )}
                </>
              )}
              {result.bre_status === "REJECTED" && (
                <>
                  <div className="text-3xl">❌</div>
                  <p className="font-semibold text-red-700">Not Eligible</p>
                  {result.rejection_reasons.length > 0 && (
                    <ul className="text-sm text-gray-600 text-left list-disc list-inside space-y-1 mt-2">
                      {result.rejection_reasons.map((r, i) => (
                        <li key={i}>{r}</li>
                      ))}
                    </ul>
                  )}
                </>
              )}
              {result.bre_status === "ERROR" && (
                <>
                  <div className="text-3xl">⚠️</div>
                  <p className="font-semibold text-yellow-700">Check Unavailable</p>
                  <p className="text-sm text-gray-500">Could not complete the eligibility check.</p>
                </>
              )}
            </div>
          )}

          {/* Action Buttons */}
          {!result ? (
            <button
              onClick={handleClick}
              disabled={loading}
              className="w-full py-3 px-6 bg-intellend-green text-white font-semibold rounded-lg hover:bg-intellend-green/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  Checking Eligibility…
                </span>
              ) : (
                error ? "Retry" : `Check Eligibility (bureau: ${bureau})`
              )}
            </button>
          ) : (
            <button
              onClick={handleContinue}
              className="w-full py-3 px-6 bg-intellend-green text-white font-semibold rounded-lg hover:bg-intellend-green/90 transition-colors"
            >
              Continue →
            </button>
          )}
        </div>
      </main>
    </div>
  );
};

export default NiyoginBRE;
